import {UploadMetadata, getDownloadURL, ref, uploadBytesResumable} from 'firebase/storage';
import {useState} from 'react';
import {useStorage} from 'reactfire';

export type FileData = Blob;

/**
 * Uploads a file to a storage path.
 * @param data The file data to upload.
 * @param metadata Optional metadata for the file.
 * @returns A promise that resolves with the download URL of the uploaded file.
 */
type FileUploadFunction = (data: FileData, metadata?: UploadMetadata) => Promise<string>;

export type UseFileUpload = {
  downloadUrl?: string;
  percentComplete: number;
  uploadFile: FileUploadFunction;
  uploadInProgress: boolean;
};

export function useFileUpload(path: string, ...segments: string[]): UseFileUpload {
  const storage = useStorage();
  const fullPath = [path, ...segments].join('/');
  const fileRef = ref(storage, fullPath);
  const [percentComplete, setPercentComplete] = useState<number>(0);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);

  const uploadFile = async (data: FileData, metadata?: UploadMetadata): Promise<string> => {
    setPercentComplete(0);
    setDownloadUrl(undefined);
    setUploadInProgress(true);
    const uploadTask = uploadBytesResumable(fileRef, data, metadata);
    uploadTask.on('state_changed', (snapshot) => {
      const {bytesTransferred, totalBytes} = snapshot;
      setPercentComplete(Math.round((bytesTransferred / totalBytes) * 100));
    });

    try {
      await uploadTask;
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      setDownloadUrl(url);
      setUploadInProgress(false);
      return url;
    } catch (error) {
      setUploadInProgress(false);
      throw error;
    }
  };

  return {downloadUrl, percentComplete, uploadFile, uploadInProgress};
}
